import React from 'react';
import { PlayCircle } from '@mezzoforte/forge-icons';
import { Box, Carousel, CarouselLeftButton, CarouselRightButton, Flex } from '@mezzoforte/forge';
import { CarouselItem } from '@/components/ImageCarousel/CarouselItem';
import { Picture } from '@/components/Image/Picture';

function VideoIndicator(props: { hasVideoId: boolean }) {
  if (!props.hasVideoId) return null;
  return (
    <Flex
      position="relative"
      backgroundColor="transparent"
      height="100%"
      justify="center"
      alignItems="center"
    >
      <PlayCircle
        height={32}
        width={32}
        color="background"
        weight="fill"
      />
    </Flex>
  );
}

function ActiveIndicator(props: { isActive: boolean }) {
  if (!props.isActive) return null;
  return (
    <Box
      height={1}
      width="100%"
      backgroundColor="brand"
      opacity={0.95}
      position="absolute"
      bottom={0}
    />
  );
}

interface ThumbnailProps {
  item: CarouselItem;
  index: number;
  current: number;
}

function Thumbnail({ item, index, current }: ThumbnailProps) {
  return (
    <Box
      aspectRatio="4 / 3"
      position="relative"
      width="auto"
      height="auto"
      minHeight={{ base: '-webkit-fill-available', md: '100%' }}
      minWidth={{ base: '-webkit-fill-available', md: '100%' }}
      borderRadius={{ base: 0, md: '4px' }}
      overflow="hidden"
      cursor="pointer"
    >
      <Picture
        formats={item.thumbnail}
        style={{ objectFit: 'cover', pointerEvents: 'none' }}
      />
      <VideoIndicator hasVideoId={!!item.videoId} />
      <ActiveIndicator isActive={current === index} />
    </Box>
  );
}

const paginationButtonBaseStyles = {
  bg: 'brandSecondary',
  bottom: '4px',
  color: 'background',
  opacity: 0,
  position: 'absolute' as const,
  top: '4px',
  transition: 'opacity 0.3s',
  _hover: {
    bg: 'brandSecondary',
    color: 'background',
  },
  _active: {
    background: 'brandSecondary',
    color: 'background',
  },
  _focus: {
    boxShadow: 'rgb(20, 44, 16, 0.6) 0px 0px 0px 3px',
  },
};

function CarouselLeft(props: React.ComponentProps<typeof CarouselLeftButton>) {
  return (
    <CarouselLeftButton
      {...paginationButtonBaseStyles}
      left={0}
      ml="-2px"
      {...props}
    />
  );
}

function CarouselRight(props: React.ComponentProps<typeof CarouselRightButton>) {
  return (
    <CarouselRightButton
      {...paginationButtonBaseStyles}
      right={0}
      mr="-2px"
      {...props}
    />
  );
}

interface CarouselThumbnailsProps {
  items: CarouselItem[];
  currentIndex: number;
  hideButtons: boolean;
  jumpTo: (index: number) => void;
}

export default function CarouselThumbnails({ items, currentIndex, hideButtons, jumpTo }: CarouselThumbnailsProps) {
  return (
    <Box
      height="auto"
      position="relative"
      _hover={!hideButtons ? { '> button': { opacity: 0.95 } } : {}}
      mt={3}
      mb={5}
    >
      <CarouselLeft />
      <Carousel
        constraints={[6, 7, 7]}
        gap={16}
        jumpTo={jumpTo}
      >
        {items.map((item, index) => (
          <Thumbnail
            key={item.key}
            item={item}
            index={index}
            current={currentIndex}
          />
        ))}
      </Carousel>
      <CarouselRight />
    </Box>
  );
}
